import isEmpty from 'lodash/isEmpty'
import isNumber from 'lodash/isNumber'
import isString from 'lodash/isString'
import isPlainObject from 'lodash/isPlainObject'
import isArray from 'lodash/isArray'
import isNil from 'lodash/isNil'
import uniq from 'lodash/uniq'
import { checkObject } from 'src/util'
import { formatDuration } from '../locale'

// found on https://emailregex.com/
const emailRegex = new RegExp(
  '^(([^<>()\\[\\]\\\\.,;:\\s@"]+(\\.[^<>()\\[\\]\\\\.,;:\\s@"]+)*)|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}' +
    '\\.[0-9]{1,3}\\.[0-9]{1,3}])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$'
)

const html5Codes = {
  required: 'WEB_REQUIRED_VALIDATOR',
  min: 'WEB_MIN_VALIDATOR',
  max: 'WEB_MAX_VALIDATOR',
  minLength: 'WEB_MIN_LENGTH_VALIDATOR',
  maxLength: 'WEB_MAX_LENGTH_VALIDATOR',
  pattern: 'WEB_PATTERN_VALIDATOR',
  email: 'WEB_EMAIL_VALIDATOR',
}

/**
 * Validate a value, given an array of validators
 * @param {*} validators
 */
export const validate = validators => (value, allValues) =>
  validators
    .map(validator => {
      const result = validator(value, allValues)
      // if no error present in result, return undefined
      return result && !!Object.values(result).filter(Boolean).length ? result : undefined
    })
    .find(Boolean)

export class ValidationError {
  constructor(message, params) {
    this.message = message
    this.params = params
  }

  translate(intl) {
    return intl.formatMessage({ id: html5Codes[this.message] || this.message }, this.params)
  }
}

export const createValidationError = (message, params) => {
  if (!message) throw new Error('message expected')
  return new ValidationError(message, params)
}

/**
 * Check if an object contains a validation error
 */
export function hasValidationError(object) {
  return checkObject(object, value => value instanceof ValidationError)
}

/**
 * HTML5-like required validator
 */
export const required = value => {
  const hasError = value =>
    isNil(value) ||
    ((isPlainObject(value) || isArray(value)) && isEmpty(value)) ||
    (isString(value) && isEmpty(value.trim()))
  if ((Array.isArray(value) && (!value.length || value.some(hasError))) || hasError(value)) {
    return createValidationError('required')
  }
}

/**
 * HTML5-like min validator
 */
export const min = min => value => {
  if (isNumber(value) && value < min) {
    return createValidationError('min', { min })
  }
}

/**
 * HTML5-like max validator
 */
export const max = max => value => {
  if (isNumber(value) && value > max) {
    return createValidationError('max', { max })
  }
}

/**
 * HTML5-like minLength validator
 */
export const minLength = minLength => value => {
  if ((isString(value) || isArray(value)) && value.length < minLength) {
    return createValidationError('minLength', { minLength, length: value.length })
  }
}

/**
 * HTML5-like maxLength validator
 */
export const maxLength = maxLength => value => {
  if ((isString(value) || isArray(value)) && value.length > maxLength) {
    return createValidationError('maxLength', { maxLength, length: value.length })
  }
}

/**
 * HTML5-like pattern validator
 */
export const pattern = pattern => value => {
  if (isString(value)) {
    const invalid = uniq(value.replace(pattern, '').split(''))
    if (invalid.length) {
      return createValidationError('pattern', { invalid: invalid.join('') })
    }
  }
}

/**
 * Custom email validator
 */
export const email = value => {
  if (isString(value) && !value.match(emailRegex)) {
    return createValidationError('email')
  }
}

/**
 * Custom min duration validator
 */
export const minDuration = min => value => {
  if (isNumber(value) && value < min) {
    return createValidationError('min', { min: formatDuration(min) })
  }
}
